import { useEffect, useState, useMemo } from "react";
import { Form, Radio, Space, Select } from "antd";
import { getLangList } from '@/lib/i18n';
import SectionConfig from "@/config/section";
import SubjectConfig from "@/config/subject";
import { modalFromLayout, getOptionArray, getDataArray, selectFilterOption } from "@/lib/antdElementFunction";
import { ExamType } from "@/containers/organization/semesterManagement/types";
import { SubjectType } from '@/containers/organization/subjectManagement/types';
import { ClassType, ClassSubjectInfo, ClassSubjectListType } from './types';

const { getClassSubject } = SectionConfig;
const { getSubjects } = SubjectConfig;

interface Props {
  form: any;
  info: ClassType;
  examList: ExamType[],
}

function ModalSubject(props: Props) {
  const { form, info, examList } = props;
  const [subjectArray, setSubjectArray] = useState<SubjectType[]>([]);
  const [subjectInfo, setSubjectInfo] = useState<ClassSubjectListType>({});
  const [template, setTemplate] = useState(1);
  const subjectOptions = useMemo(() => getOptionArray(getDataArray(subjectArray, 'subjectId', 'enName')), [subjectArray]);
  const subjectT: any = getLangList("subject");
  const semesterT: any = getLangList("semester");

  useEffect(() => {
    const { classId, template } = info ?? {};

    function getClassSubjectFunction() {
      getClassSubject({ classId }).then((res: any) => {
        const subjectInfo = res?.value ?? [];

        setSubjectInfo(subjectInfo);
        if (Object.keys(subjectInfo).length > 0) {
          const examId = parseInt(Object.keys(subjectInfo)?.[0]) ?? 0;
          const { SI, SII, SIII, OP, S, C, LA } = subjectInfo[examId];

          form.setFieldsValue({
            examId,
            SI: formateSubjectInfo(SI),
            SII: formateSubjectInfo(SII),
            SIII: formateSubjectInfo(SIII),
            OP: formateSubjectInfo(OP),
            S: formateSubjectInfo(S),
            C: formateSubjectInfo(C),
            LA: formateSubjectInfo(LA),
          });
        }
      });
    };

    function getSubjectsFunction() {
      getSubjects().then((res: any) => {
        const subjectArray = res?.value ?? [];
        setSubjectArray(subjectArray);
      });
    };

    form.setFieldsValue({ template });
    setTemplate(template);
    getClassSubjectFunction();
    getSubjectsFunction();
  }, []);

  function onChangeTemplate(e: any) {
    const { template } = info ?? {};
    const { examId } = form.getFieldsValue();

    if (e.target.value === template) {
      initSubjectInfo(examId);
    } else {
      form.setFieldsValue({ SI: [], SII: [], SIII: [], OP: [], S: [], C: [], LA: [] });
    }
  };

  function initSubjectInfo(examId: number) {
    const { SI = [], SII = [], SIII = [], OP = [], S = [], C = [], LA = [] } = subjectInfo?.[examId] ?? {};

    form.setFieldsValue({
      SI: formateSubjectInfo(SI),
      SII: formateSubjectInfo(SII),
      SIII: formateSubjectInfo(SIII),
      OP: formateSubjectInfo(OP),
      S: formateSubjectInfo(S),
      C: formateSubjectInfo(C),
      LA: formateSubjectInfo(LA),
    });
  };

  function formateSubjectInfo(subject: ClassSubjectInfo[]) {
    return subject?.map((item) => item.subjectId) ?? [];
  };

  return (
    <Form
      className="admin-form"
      style={{ marginTop: 20 }}
      {...modalFromLayout}
      form={form}
    >
      <Form.Item
        label={semesterT.EXAM}
        name="examId"
        rules={[{ required: true }]}
      >
        <Radio.Group size="small" onChange={(e) => initSubjectInfo(e.target.value)}>
          {examList?.map((exam) => <Radio.Button value={exam.examId} key={exam.examId}>{exam.examName}</Radio.Button>)}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={subjectT.PDF_TEMPLATE}
        name="template"
        rules={[{ required: true }]}
      >
        <Radio.Group size="small" onChange={(e) => {
          setTemplate(e.target.value);
          onChangeTemplate(e);
        }}>
          <Space align="center" wrap>
            <Radio value={1}>{subjectT.TEMPLATE_1}</Radio>
            <Radio value={2}>{subjectT.TEMPLATE_2}</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>

      {template === 1
        ? (
          <>
            <Form.Item name="SI" label={subjectT.SI} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>

            <Form.Item name="SII" label={subjectT.SII} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>

            <Form.Item name="SIII" label={subjectT.SIII} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>

            <Form.Item name="OP" label={subjectT.OP} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>
          </>
        )
        : (
          <>
            <Form.Item name="S" label={subjectT.S} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>

            <Form.Item name="C" label={subjectT.C} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>

            <Form.Item name="LA" label={subjectT.LA} rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="children"
                mode="multiple"
                filterOption={(input: any, option: any) => selectFilterOption(input, option)}
              >
                {subjectOptions}
              </Select>
            </Form.Item>
          </>
        )
      }
    </Form >
  );
}

export default ModalSubject;